import { PayloadAction } from '@reduxjs/toolkit';
import { AddContactInfoRequest } from '@/types/entities/contactInfo';
import { AddAddressRequest } from '@/domains/addresses/types/api/requests/AddAddressRequest';
import { ProducerEntityType } from '@/domains/producers/types/ProducerEntityType';
import { call, put, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { getProducerDetails } from '@/features/entityDetails/store/entityDetailsSlice';
import { resetGeographyState } from '@/features/geography/store/geographySlice';
import { resetAddressValidationState } from '@/features/googleApi/addressValidationApi/store/googleAddressValidationApiSlice';
import { resetGooglePlacesState } from '@/features/googleApi/placesApi/store/googlePlacesApiSlice';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { addProducerAddress, addProducerContactInfo } from '@/domains/producer/store/producer.slice';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* addProducerAddressSaga({
  payload,
}: PayloadAction<AddAddressRequest & { producerEntityType: ProducerEntityType }>) {
  try {
    const { producerEntityType, ...data } = payload;
    yield put(setSystemLoadingStatus(true));
    yield call(
      HttpService.post,
      `/examiner/producer/add-address-to-producer${producerEntityType === EdsEntityCodes.ProducerGroup ? '_group' : ''}`,
      data,
    );
    SnacksService.add({
      type: SnackType.Success,
      title: 'Updates saved successfully!',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(getProducerDetails({ id: `${payload.entityId}`, producerEntityType }));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetGeographyState());
    yield put(resetAddressValidationState());
    yield put(resetGooglePlacesState());
    ModalService.close(Modals.AddAddress);
  }
}

function* addProducerContactInfoSaga({
  payload,
}: PayloadAction<AddContactInfoRequest & { producerEntityType: ProducerEntityType }>) {
  try {
    const { producerEntityType, ...data } = payload;
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/producers/add-contact-information`, data);
    yield put(getProducerDetails({ id: `${payload.entityId}`, producerEntityType }));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.AddContactInfo);
  }
}

export function* producerWatcher() {
  yield takeLatest(addProducerAddress.type, addProducerAddressSaga);
  yield takeLatest(addProducerContactInfo.type, addProducerContactInfoSaga);
}
